'use client';
import React, { useState } from 'react';
import { ClipboardList, Search, Filter, ChevronDown, X, Download, Printer, ChevronRight, ChevronLeft, Clock, Car, CreditCard, Phone, DoorOpen, ArrowUpDown, Eye, Shield, AlertTriangle, CheckCircle, Users, Calendar,  } from 'lucide-react';
import { mockVisits, type Visit, type VisitorStatus, type VisitorType, type GateNumber, type VisitPurpose } from '@/data/mockData';
import { StatusBadge, TypeBadge } from '@/components/ui/StatusBadge';
import { Toaster, toast } from 'sonner';
import VisitDetailModal from './VisitDetailModal';

function formatDateTime(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, '0');
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const yy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return { date: `${dd}/${mo}/${yy}`, time: `${hh}:${mm}` };
}

type SortField = 'entryTime' | 'visitorName' | 'company' | 'hostDepartment' | 'duration' | 'status';
type SortDir = 'asc' | 'desc';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

export default function VisitorsLogContent() {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<VisitorStatus | 'الكل'>('الكل');
  const [filterType, setFilterType] = useState<VisitorType | 'الكل'>('الكل');
  const [filterGate, setFilterGate] = useState<GateNumber | 'الكل'>('الكل');
  const [filterPurpose, setFilterPurpose] = useState<VisitPurpose | 'الكل'>('الكل');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [sortField, setSortField] = useState<SortField>('entryTime');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedVisit, setSelectedVisit] = useState<Visit | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  const statuses: (VisitorStatus | 'الكل')[] = ['الكل', 'داخل', 'خرج', 'منتهية الصلاحية', 'ملغاة'];
  const visitorTypes: (VisitorType | 'الكل')[] = ['الكل', 'زائر', 'مورد', 'مقاول', 'عميل', 'سائق شاحنة', 'عامل خارجي'];
  const gates: (GateNumber | 'الكل')[] = ['الكل', 'البوابة الرئيسية', 'بوابة الشاحنات', 'البوابة الخلفية'];
  const purposes: (VisitPurpose | 'الكل')[] = ['الكل', 'اجتماع عمل', 'توريد مواد', 'صيانة', 'تفتيش', 'زيارة رسمية', 'تسليم شحنة'];

  const filtered = mockVisits
    .filter((v) => {
      const q = searchQuery.trim().toLowerCase();
      const matchSearch =
        !q ||
        v.visitorName.includes(searchQuery) ||
        v.company.includes(searchQuery) ||
        v.nationalId.includes(q) ||
        v.mobile.includes(q) ||
        v.hostDepartment.includes(searchQuery) ||
        v.hostEmployee.includes(searchQuery) ||
        v.badgeNumber.toLowerCase().includes(q);
      const matchStatus = filterStatus === 'الكل' || v.status === filterStatus;
      const matchType = filterType === 'الكل' || v.type === filterType;
      const matchGate = filterGate === 'الكل' || v.gate === filterGate;
      const matchPurpose = filterPurpose === 'الكل' || v.purpose === filterPurpose;
      let matchDate = true;
      if (filterDateFrom) {
        matchDate = matchDate && v.entryTime >= filterDateFrom;
      }
      if (filterDateTo) {
        matchDate = matchDate && v.entryTime <= filterDateTo + 'T23:59:59';
      }
      return matchSearch && matchStatus && matchType && matchGate && matchPurpose && matchDate;
    })
    .sort((a, b) => {
      let cmp = 0;
      if (sortField === 'entryTime') {
        cmp = new Date(a.entryTime).getTime() - new Date(b.entryTime).getTime();
      } else if (sortField === 'visitorName') {
        cmp = a.visitorName.localeCompare(b.visitorName, 'ar');
      } else if (sortField === 'company') {
        cmp = a.company.localeCompare(b.company, 'ar');
      } else if (sortField === 'hostDepartment') {
        cmp = a.hostDepartment.localeCompare(b.hostDepartment, 'ar');
      } else if (sortField === 'status') {
        cmp = a.status.localeCompare(b.status, 'ar');
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
    setCurrentPage(1);
  };

  const handleFilterChange = () => {
    setCurrentPage(1);
  };

  const activeFilterCount = [
    filterStatus !== 'الكل',
    filterType !== 'الكل',
    filterGate !== 'الكل',
    filterPurpose !== 'الكل',
    !!filterDateFrom,
    !!filterDateTo,
  ].filter(Boolean).length;

  const handleExport = (format: 'pdf' | 'excel') => {
    // TODO: Backend — GET /api/visits/export?format=pdf&filters=... returns downloadable file
    toast.success(`جاري تصدير ${filtered.length} سجل بصيغة ${format === 'pdf' ? 'PDF' : 'Excel'}...`);
  };

  const SortTh = ({ field, label, className = '' }: { field: SortField; label: string; className?: string }) => (
    <th className={`text-right px-4 py-3 ${className}`}>
      <button
        onClick={() => handleSort(field)}
        className={`flex items-center gap-1 text-xs font-semibold uppercase tracking-wide transition-colors ${
          sortField === field ? 'text-accent' : 'text-muted-foreground hover:text-foreground'
        }`}
      >
        {label}
        <ArrowUpDown size={10} className={sortField === field ? 'text-accent' : 'text-muted-foreground'} />
      </button>
    </th>
  );

  const statsToday = mockVisits.filter((v) => v.entryTime.startsWith('2026-08-09')).length;
  const statsInside = mockVisits.filter((v) => v.status === 'داخل').length;
  const statsOverstay = mockVisits.filter((v) => v.status === 'منتهية الصلاحية').length;
  const statsTotal = mockVisits.length;

  return (
    <div className="min-h-screen bg-background p-4 xl:p-6" dir="rtl">
      <Toaster position="bottom-right" richColors />

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-info/15 border border-info/30 flex items-center justify-center">
            <ClipboardList size={20} className="text-info" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">سجل الزوار</h1>
            <p className="text-sm text-muted-foreground">السجل التاريخي الكامل لجميع الزيارات</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleExport('excel')}
            className="flex items-center gap-2 px-4 py-2.5 bg-card border border-border rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground hover:border-accent/40 transition-all duration-150 active:scale-95"
          >
            <Download size={14} />
            Excel
          </button>
          <button
            onClick={() => handleExport('pdf')}
            className="flex items-center gap-2 px-4 py-2.5 bg-card border border-border rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground hover:border-accent/40 transition-all duration-150 active:scale-95"
          >
            <Printer size={14} />
            PDF
          </button>
        </div>
      </div>

      {/* Summary KPI strip — 4 cards, grid-cols-4 */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-5">
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1">إجمالي السجلات</p>
          <p className="text-3xl font-bold text-foreground tabular-nums">{statsTotal}</p>
          <p className="text-xs text-muted-foreground mt-1">كل الزيارات</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1">اليوم</p>
          <p className="text-3xl font-bold text-accent tabular-nums">{statsToday}</p>
          <p className="text-xs text-muted-foreground mt-1">09 أغسطس 2026</p>
        </div>
        <div className="bg-card border border-success/30 rounded-xl p-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1">داخل الآن</p>
          <p className="text-3xl font-bold text-success tabular-nums">{statsInside}</p>
          <p className="text-xs text-muted-foreground mt-1">موجود حالياً</p>
        </div>
        <div className={`bg-card rounded-xl p-4 ${statsOverstay > 0 ? 'border border-warning/40' : 'border border-border'}`}>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1">منتهية الصلاحية</p>
          <p className={`text-3xl font-bold tabular-nums ${statsOverstay > 0 ? 'text-warning' : 'text-muted-foreground'}`}>{statsOverstay}</p>
          <p className="text-xs text-muted-foreground mt-1">تجاوزوا الوقت</p>
        </div>
      </div>

      {/* Search + filter bar */}
      <div className="bg-card border border-border rounded-2xl p-4 mb-4">
        <div className="flex gap-3 flex-col sm:flex-row">
          <div className="relative flex-1">
            <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); handleFilterChange(); }}
              placeholder="بحث بالاسم، الجهة، الرقم القومي، رقم الكارت..."
              className="w-full bg-input border border-border rounded-xl pr-9 pl-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {searchQuery && (
              <button onClick={() => { setSearchQuery(''); handleFilterChange(); }} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X size={14} />
              </button>
            )}
          </div>

          {/* Quick status filter chips */}
          <div className="flex gap-1.5 flex-wrap">
            {statuses.map((s) => (
              <button
                key={`status-chip-${s}`}
                onClick={() => { setFilterStatus(s); handleFilterChange(); }}
                className={`px-3 py-2 rounded-xl text-xs font-semibold transition-all duration-150 whitespace-nowrap ${
                  filterStatus === s
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/70'
                }`}
              >
                {s === 'الكل' ? 'الكل' : s}
              </button>
            ))}
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 whitespace-nowrap ${
              showFilters || activeFilterCount > 0
                ? 'bg-accent/15 border border-accent/30 text-accent' :'bg-muted border border-border text-muted-foreground hover:text-foreground'
            }`}
          >
            <Filter size={14} />
            فلاتر متقدمة
            {activeFilterCount > 0 && (
              <span className="bg-accent text-accent-foreground text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center tabular-nums">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {/* Advanced filters panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-border grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 fade-in">
            {/* Type */}
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">نوع الزائر</label>
              <div className="relative">
                <Users size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <select
                  value={filterType}
                  onChange={(e) => { setFilterType(e.target.value as VisitorType | 'الكل'); handleFilterChange(); }}
                  className="w-full bg-input border border-border rounded-xl pr-8 pl-7 py-2.5 text-sm text-foreground appearance-none focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  {visitorTypes.map((t) => (
                    <option key={`adv-type-${t}`} value={t}>{t === 'الكل' ? 'كل الأنواع' : t}</option>
                  ))}
                </select>
                <ChevronDown size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              </div>
            </div>

            {/* Gate */}
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">البوابة</label>
              <div className="relative">
                <DoorOpen size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <select
                  value={filterGate}
                  onChange={(e) => { setFilterGate(e.target.value as GateNumber | 'الكل'); handleFilterChange(); }}
                  className="w-full bg-input border border-border rounded-xl pr-8 pl-7 py-2.5 text-sm text-foreground appearance-none focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  {gates.map((g) => (
                    <option key={`adv-gate-${g}`} value={g}>{g === 'الكل' ? 'كل البوابات' : g}</option>
                  ))}
                </select>
                <ChevronDown size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              </div>
            </div>

            {/* Purpose */}
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">غرض الزيارة</label>
              <div className="relative">
                <ClipboardList size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <select
                  value={filterPurpose}
                  onChange={(e) => { setFilterPurpose(e.target.value as VisitPurpose | 'الكل'); handleFilterChange(); }}
                  className="w-full bg-input border border-border rounded-xl pr-8 pl-7 py-2.5 text-sm text-foreground appearance-none focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  {purposes.map((p) => (
                    <option key={`adv-purpose-${p}`} value={p}>{p === 'الكل' ? 'كل الأغراض' : p}</option>
                  ))}
                </select>
                <ChevronDown size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              </div>
            </div>

            {/* Date range */}
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">نطاق التاريخ</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Calendar size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <input
                    type="date"
                    value={filterDateFrom}
                    onChange={(e) => { setFilterDateFrom(e.target.value); handleFilterChange(); }}
                    className="w-full bg-input border border-border rounded-xl pr-8 pl-2 py-2.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div className="relative flex-1">
                  <input
                    type="date"
                    value={filterDateTo}
                    onChange={(e) => { setFilterDateTo(e.target.value); handleFilterChange(); }}
                    className="w-full bg-input border border-border rounded-xl px-2 py-2.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              </div>
            </div>

            {/* Reset */}
            {activeFilterCount > 0 && (
              <div className="sm:col-span-2 xl:col-span-4 flex justify-end">
                <button
                  onClick={() => {
                    setFilterStatus('الكل');
                    setFilterType('الكل');
                    setFilterGate('الكل');
                    setFilterPurpose('الكل');
                    setFilterDateFrom('');
                    setFilterDateTo('');
                    handleFilterChange();
                  }}
                  className="flex items-center gap-1.5 text-xs text-danger hover:text-danger/70 transition-colors"
                >
                  <X size={12} />
                  إعادة تعيين جميع الفلاتر ({activeFilterCount})
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <h3 className="font-bold text-foreground flex items-center gap-2">
              <ClipboardList size={16} className="text-accent" />
              سجل الزيارات
            </h3>
            <span className="bg-accent/15 text-accent text-xs font-bold px-2 py-0.5 rounded-full tabular-nums">
              {filtered.length} نتيجة
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">عرض:</span>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
              className="bg-input border border-border rounded-lg px-2 py-1 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {PAGE_SIZE_OPTIONS.map((s) => (
                <option key={`page-size-${s}`} value={s}>{s} سجل</option>
              ))}
            </select>
          </div>
        </div>

        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full min-w-[1100px]">
            <thead>
              <tr className="bg-muted/40 border-b border-border">
                <th className="text-right px-4 py-3 w-8">
                  <span className="text-xs text-muted-foreground">#</span>
                </th>
                <SortTh field="visitorName" label="الاسم" />
                <SortTh field="company" label="الجهة" />
                <th className="text-right px-4 py-3">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">النوع</span>
                </th>
                <th className="text-right px-4 py-3">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">الغرض</span>
                </th>
                <SortTh field="hostDepartment" label="القسم المضيف" />
                <th className="text-right px-4 py-3">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">البوابة</span>
                </th>
                <SortTh field="entryTime" label="وقت الدخول" />
                <th className="text-right px-4 py-3">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">وقت الخروج</span>
                </th>
                <th className="text-right px-4 py-3">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">المدة</span>
                </th>
                <th className="text-right px-4 py-3">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">الكارت</span>
                </th>
                <SortTh field="status" label="الحالة" />
                <th className="text-right px-4 py-3 w-20">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">إجراء</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={13} className="text-center py-20">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center">
                        <ClipboardList size={28} className="text-muted-foreground" />
                      </div>
                      <p className="text-foreground font-semibold text-lg">لا توجد زيارات مطابقة</p>
                      <p className="text-sm text-muted-foreground max-w-xs text-center">
                        لم يُعثر على زيارات تطابق معايير البحث والفلترة المحددة. جرّب تعديل الفلاتر أو مسحها.
                      </p>
                      <button
                        onClick={() => {
                          setSearchQuery('');
                          setFilterStatus('الكل');
                          setFilterType('الكل');
                          setFilterGate('الكل');
                          setFilterPurpose('الكل');
                          setFilterDateFrom('');
                          setFilterDateTo('');
                        }}
                        className="mt-2 px-4 py-2 bg-accent/15 border border-accent/30 text-accent rounded-xl text-sm font-medium hover:bg-accent/25 transition-all"
                      >
                        مسح جميع الفلاتر
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                paginated.map((visit, index) => {
                  const entry = formatDateTime(visit.entryTime);
                  const exit = visit.exitTime ? formatDateTime(visit.exitTime) : null;
                  const rowNum = (currentPage - 1) * pageSize + index + 1;
                  return (
                    <tr
                      key={`log-row-${visit.id}`}
                      className={`border-b border-border last:border-0 transition-colors duration-100 hover:bg-muted/30 group ${
                        visit.status === 'منتهية الصلاحية' ? 'bg-warning/5' : index % 2 === 0 ? '' : 'bg-muted/10'
                      }`}
                    >
                      <td className="px-4 py-3 text-xs text-muted-foreground tabular-nums">{rowNum}</td>

                      {/* Name */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                            <Shield size={12} className="text-accent" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-foreground whitespace-nowrap">{visit.visitorName}</p>
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <Phone size={9} />
                              {visit.mobile}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Company */}
                      <td className="px-4 py-3">
                        <p className="text-sm text-foreground max-w-[140px] truncate" title={visit.company}>{visit.company}</p>
                      </td>

                      {/* Type */}
                      <td className="px-4 py-3">
                        <TypeBadge type={visit.type} size="sm" />
                      </td>

                      {/* Purpose */}
                      <td className="px-4 py-3">
                        <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-lg whitespace-nowrap">{visit.purpose}</span>
                      </td>

                      {/* Host dept */}
                      <td className="px-4 py-3">
                        <p className="text-sm text-foreground whitespace-nowrap">{visit.hostDepartment}</p>
                        <p className="text-xs text-muted-foreground truncate max-w-[120px]">{visit.hostEmployee}</p>
                      </td>

                      {/* Gate */}
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap">
                          <DoorOpen size={11} />
                          {visit.gate.replace('البوابة ', '').replace('بوابة ', '')}
                        </span>
                        <p className="text-xs text-muted-foreground mt-0.5">{visit.securityOfficer}</p>
                      </td>

                      {/* Entry time */}
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold text-foreground tabular-nums flex items-center gap-1">
                          <Clock size={11} className="text-accent" />
                          {entry.time}
                        </p>
                        <p className="text-xs text-muted-foreground tabular-nums">{entry.date}</p>
                      </td>

                      {/* Exit time */}
                      <td className="px-4 py-3">
                        {exit ? (
                          <>
                            <p className="text-sm font-semibold text-muted-foreground tabular-nums">{exit.time}</p>
                            <p className="text-xs text-muted-foreground tabular-nums">{exit.date}</p>
                          </>
                        ) : (
                          <span className="text-xs text-muted-foreground/50 italic">—</span>
                        )}
                      </td>

                      {/* Duration */}
                      <td className="px-4 py-3">
                        {visit.duration ? (
                          <span className="text-sm font-semibold text-foreground tabular-nums">{visit.duration}</span>
                        ) : visit.status === 'داخل' ? (
                          <span className="text-sm font-semibold text-success tabular-nums flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
                            جارية
                          </span>
                        ) : visit.status === 'منتهية الصلاحية' ? (
                          <span className="text-sm font-semibold text-warning tabular-nums flex items-center gap-1">
                            <AlertTriangle size={11} />
                            متجاوزة
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground/50">—</span>
                        )}
                      </td>

                      {/* Badge */}
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1 text-xs font-mono text-accent">
                          <CreditCard size={10} />
                          {visit.badgeNumber}
                        </span>
                        {visit.vehiclePlate && (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                            <Car size={9} />
                            {visit.vehiclePlate}
                          </span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">
                        <StatusBadge status={visit.status} size="sm" />
                        {visit.safetyAcknowledged && (
                          <p className="text-xs text-success flex items-center gap-1 mt-1">
                            <CheckCircle size={9} />
                            إقرار موقّع
                          </p>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setSelectedVisit(visit)}
                          className="opacity-0 group-hover:opacity-100 flex items-center gap-1.5 px-3 py-1.5 bg-accent/10 border border-accent/20 text-accent rounded-lg text-xs font-medium hover:bg-accent/20 transition-all duration-150 active:scale-95"
                          title="عرض تفاصيل الزيارة"
                        >
                          <Eye size={12} />
                          تفاصيل
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filtered.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-5 py-4 border-t border-border">
            <p className="text-xs text-muted-foreground">
              عرض{' '}
              <span className="text-foreground font-semibold tabular-nums">
                {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filtered.length)}
              </span>{' '}
              من{' '}
              <span className="text-foreground font-semibold tabular-nums">{filtered.length}</span>{' '}
              سجل
              {filtered.length !== mockVisits.length && (
                <span className="text-muted-foreground"> (من إجمالي {mockVisits.length})</span>
              )}
            </p>

            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                aria-label="الصفحة الأولى"
              >
                <ChevronRight size={14} />
                <ChevronRight size={14} className="-mr-2.5" />
              </button>
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                aria-label="الصفحة السابقة"
              >
                <ChevronRight size={14} />
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let page: number;
                if (totalPages <= 5) {
                  page = i + 1;
                } else if (currentPage <= 3) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = currentPage - 2 + i;
                }
                return (
                  <button
                    key={`page-btn-${page}`}
                    onClick={() => setCurrentPage(page)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-semibold transition-all duration-150 tabular-nums ${
                      currentPage === page
                        ? 'bg-accent text-accent-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}

              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                aria-label="الصفحة التالية"
              >
                <ChevronLeft size={14} />
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                aria-label="الصفحة الأخيرة"
              >
                <ChevronLeft size={14} />
                <ChevronLeft size={14} className="-ml-2.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Visit detail modal */}
      {selectedVisit && (
        <VisitDetailModal visit={selectedVisit} onClose={() => setSelectedVisit(null)} />
      )}
    </div>
  );
}